import React, { useState, useRef } from "react";
import { apiService } from "../services/apiService";
import { FileUploadUser } from "../types/auth";
import "./FileUpload.css";

interface FileUploadProps {
  user: FileUploadUser;
  onUploadSuccess?: (filename: string) => void;
  onUploadError?: (error: string) => void;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  user,
  onUploadSuccess,
  onUploadError,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [message, setMessage] = useState<string>("");
  const [messageType, setMessageType] = useState<"success" | "error" | "">("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setMessage("");
      setMessageType("");
      setUploadProgress(0);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      setSelectedFile(files[0]);
      setMessage("");
      setMessageType("");
      setUploadProgress(0);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setMessage("Please select a file first");
      setMessageType("error");
      return;
    }

    setUploading(true);
    setMessage("");
    setMessageType("");
    setUploadProgress(0);

    try {
      // Set the auth token before making the request
      apiService.setAuthToken(user.token || null);

      await apiService.uploadFile(selectedFile, (progress) => {
        setUploadProgress(progress);
      });

      setMessage(`File "${selectedFile.name}" uploaded successfully!`);
      setMessageType("success");
      setSelectedFile(null);
      setUploadProgress(0);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      if (onUploadSuccess) {
        onUploadSuccess(selectedFile.name);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Upload failed";
      setMessage(`Upload failed: ${errorMessage}`);
      setMessageType("error");
      setUploadProgress(0);

      if (onUploadError) {
        onUploadError(errorMessage);
      }
    } finally {
      setUploading(false);
    }
  };

  const clearSelection = () => {
    setSelectedFile(null);
    setMessage("");
    setMessageType("");
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div className="file-upload-container">
      <div
        className={`file-drop-zone ${selectedFile ? "has-file" : ""}`}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          style={{ display: "none" }}
        />

        {selectedFile ? (
          <div className="file-info">
            <div className="file-icon">📄</div>
            <div className="file-details">
              <div className="file-name">{selectedFile.name}</div>
              <div className="file-size">
                {formatFileSize(selectedFile.size)}
              </div>
            </div>
            <button
              type="button"
              className="clear-file-btn"
              onClick={(e) => {
                e.stopPropagation();
                clearSelection();
              }}
              disabled={uploading}
            >
              ✕
            </button>
          </div>
        ) : (
          <div className="drop-zone-content">
            <div className="upload-icon">📁</div>
            <p>Click to select a file or drag and drop</p>
            <p className="file-hint">Any file type is supported</p>
          </div>
        )}
      </div>

      {uploading && (
        <div className="upload-progress">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
          <div className="progress-text">{uploadProgress}%</div>
        </div>
      )}

      {message && <div className={`message ${messageType}`}>{message}</div>}

      <div className="upload-actions">
        <button
          className="upload-btn"
          onClick={handleUpload}
          disabled={!selectedFile || uploading}
        >
          {uploading ? "Uploading..." : "Upload File"}
        </button>

        {selectedFile && !uploading && (
          <button className="clear-btn" onClick={clearSelection}>
            Clear
          </button>
        )}
      </div>
    </div>
  );
};
